import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PermissionMatrix } from "@/components/common/PermissionMatrix";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, KeyRound, ShieldCheck, History, Power, Lock } from "lucide-react";

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

export default function Users() {
  const { can, user: me } = useAuth();
  const isSuper = can("*");
  const { outletOptions, outletName } = useMeta();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [meta, setMeta] = useState({ roles: [], modules: [], actions: [], role_permissions: {} });

  const [form, setForm] = useState(null); // {mode:'create'|'edit', data}
  const [saving, setSaving] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [permValue, setPermValue] = useState(new Set());
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwd, setPwd] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [toggleTarget, setToggleTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/users/roles")]);
      setUsers(u.data || []);
      setMeta(r.data || { roles: [], modules: [], actions: [], role_permissions: {} });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q));
  }, [users, search]);

  const roleLabel = (k) => meta.roles.find((r) => r.value === k)?.label || String(k).replace(/_/g, " ");

  // ---- Create / Edit ----
  const openCreate = () => setForm({ mode: "create", data: { name: "", email: "", role: "staff", outlet_id: "", active: true, password: "" } });
  const openEdit = (u) => setForm({ mode: "edit", data: { ...u, outlet_id: u.outlet_id || "", password: "" } });
  const setField = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const saveForm = async () => {
    const d = form.data;
    if (!d.name?.trim()) { toast.error("Name is required"); return; }
    if (form.mode === "create" && !d.email?.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const payload = { name: d.name, role: d.role, outlet_id: d.outlet_id || null, active: d.active };
      if (form.mode === "create") {
        payload.email = d.email.trim().toLowerCase();
        if (d.password) payload.password = d.password;
        await api.post("/users", payload);
      } else {
        await api.put(`/users/${d.id}`, payload);
      }
      toast.success(form.mode === "create" ? "User created" : "User updated");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // ---- Permission overrides ----
  const basePerms = (u) => new Set(meta.role_permissions[u.role] || []);
  const openPerms = (u) => {
    setPermTarget(u);
    const base = basePerms(u);
    const val = new Set(base);
    (u.extra_permissions || []).forEach((p) => val.add(p));
    (u.denied_permissions || []).forEach((p) => val.delete(p));
    setPermValue(val);
  };
  const togglePerm = (perm, next) => setPermValue((prev) => {
    const s = new Set(prev);
    if (next) s.add(perm); else s.delete(perm);
    return s;
  });
  const savePerms = async () => {
    const base = basePerms(permTarget);
    const extra = [...permValue].filter((p) => !base.has(p));
    const denied = [...base].filter((p) => !permValue.has(p));
    setSaving(true);
    try {
      await api.put(`/users/${permTarget.id}`, { extra_permissions: extra, denied_permissions: denied });
      toast.success("Permissions updated");
      setPermTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // ---- Reset password ----
  const doResetPassword = async () => {
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${pwdTarget.id}/reset-password`, { password: pwd });
      toast.success(`Password reset for ${pwdTarget.name}`);
      setPwdTarget(null); setPwd("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // ---- Login history ----
  const openHistory = async (u) => {
    setHistoryTarget(u); setHistory([]);
    try {
      const { data } = await api.get(`/users/${u.id}/login-history`);
      setHistory(data || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- Activate / Deactivate ----
  const doToggleActive = async () => {
    const u = toggleTarget;
    setSaving(true);
    try {
      if (u.active) await api.delete(`/users/${u.id}`);
      else await api.put(`/users/${u.id}`, { active: true });
      toast.success(u.active ? "User deactivated" : "User activated");
      setToggleTarget(null); load();
    } catch (e) { toast.error(apiError(e)); setToggleTarget(null); }
  };

  const columns = [
    { key: "name", label: "User", render: (r) => (
      <div className="flex items-center gap-2.5">
        <Avatar className="h-8 w-8"><AvatarImage src={r.picture} /><AvatarFallback className="bg-sidebar text-xs text-white">{(r.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
        <div><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></div>
      </div>
    ) },
    { key: "role", label: "Role", render: (r) => (
      <span className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-0.5 text-xs font-medium capitalize">
        {r.role === "super_admin" && <Lock className="h-3 w-3" />}{roleLabel(r.role)}
      </span>
    ) },
    { key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) },
    { key: "last_login", label: "Last Login", render: (r) => <span className="tabular text-xs text-muted-foreground">{fmtDate(r.last_login)}</span> },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "active" : "inactive"} /> },
  ];

  const rowActions = (r) => {
    const targetSuper = r.role === "super_admin";
    const canManage = isSuper || (can("users:edit") && !targetSuper);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canManage && <DropdownMenuItem data-testid={`edit-user-${r.id}`} onClick={() => openEdit(r)}><Pencil className="mr-2 h-4 w-4" />Edit details</DropdownMenuItem>}
          {isSuper && !targetSuper && <DropdownMenuItem data-testid={`perms-user-${r.id}`} onClick={() => openPerms(r)}><ShieldCheck className="mr-2 h-4 w-4" />Manage permissions</DropdownMenuItem>}
          {isSuper && <DropdownMenuItem data-testid={`pwd-user-${r.id}`} onClick={() => { setPwdTarget(r); setPwd(""); }}><KeyRound className="mr-2 h-4 w-4" />Reset password</DropdownMenuItem>}
          <DropdownMenuItem data-testid={`history-user-${r.id}`} onClick={() => openHistory(r)}><History className="mr-2 h-4 w-4" />Login history</DropdownMenuItem>
          {canManage && !targetSuper && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`toggle-user-${r.id}`} className={r.active ? "text-destructive" : ""} onClick={() => setToggleTarget(r)}>
                <Power className="mr-2 h-4 w-4" />{r.active ? "Deactivate" : "Activate"}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Staff / Users" subtitle="Create staff, assign roles, control permissions, reset passwords and review activity.">
        {can("users:create") && (
          <Button data-testid="add-user-btn" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> New User</Button>
        )}
      </PageHeader>

      <DataTable
        testId="users-table"
        columns={columns}
        rows={filtered}
        loading={loading}
        total={filtered.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, email or role…"
        rowActions={rowActions}
        emptyTitle="No staff yet"
        emptyHint="Create your first user and assign a role."
      />

      {/* Create / Edit */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle>{form?.mode === "create" ? "New User" : "Edit User"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label className="eyebrow">Full Name</Label>
                <Input data-testid="user-name" value={form.data.name} onChange={(e) => setField("name", e.target.value)} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="eyebrow">Email</Label>
                <Input data-testid="user-email" type="email" value={form.data.email} disabled={form.mode === "edit"} onChange={(e) => setField("email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Role</Label>
                <Select value={form.data.role} onValueChange={(v) => setField("role", v)} disabled={form.data.role === "super_admin"}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.roles.filter((r) => r.value !== "super_admin" || isSuper).map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Assigned Outlet</Label>
                <Select value={form.data.outlet_id || "none"} onValueChange={(v) => setField("outlet_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="user-outlet"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (chain-wide)</SelectItem>
                    {outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.mode === "create" && (
                <div className="col-span-2 space-y-1.5">
                  <Label className="eyebrow">Initial Password (optional)</Label>
                  <Input data-testid="user-password" type="text" value={form.data.password} placeholder="Default: Hunter@123" onChange={(e) => setField("password", e.target.value)} />
                </div>
              )}
              <div className="col-span-2 flex items-center gap-3">
                <Switch data-testid="user-active" checked={form.data.active} disabled={form.data.role === "super_admin"} onCheckedChange={(v) => setField("active", v)} />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={saveForm} disabled={saving} data-testid="user-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permission overrides */}
      <Dialog open={!!permTarget} onOpenChange={(v) => !v && setPermTarget(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="user-perms-dialog">
          <DialogHeader>
            <DialogTitle>Permissions — {permTarget?.name}</DialogTitle>
            <DialogDescription>
              Role <strong className="capitalize">{roleLabel(permTarget?.role)}</strong> grants the dotted defaults. Toggle any cell to grant or revoke for this person specifically.
            </DialogDescription>
          </DialogHeader>
          {permTarget && (
            <PermissionMatrix
              modules={meta.modules}
              actions={meta.actions}
              value={permValue}
              baseline={basePerms(permTarget)}
              onToggle={togglePerm}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermTarget(null)}>Cancel</Button>
            <Button onClick={savePerms} disabled={saving} data-testid="perms-save">{saving ? "Saving…" : "Save Permissions"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwdTarget} onOpenChange={(v) => !v && setPwdTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-pwd-dialog">
          <DialogHeader><DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {pwdTarget?.name}. They can sign in with it immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="eyebrow">New Password</Label>
            <Input data-testid="new-password" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 6 characters" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdTarget(null)}>Cancel</Button>
            <Button onClick={doResetPassword} disabled={saving} data-testid="reset-pwd-save">{saving ? "Saving…" : "Reset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Login history */}
      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto" data-testid="login-history-dialog">
          <DialogHeader><DialogTitle>Login history — {historyTarget?.name}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No login records yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div>
                    <div className="font-medium tabular">{fmtDate(h.at)}</div>
                    <div className="text-xs text-muted-foreground">{h.ip || "—"} · {h.method}</div>
                  </div>
                  <div className="max-w-[45%] truncate text-right text-xs text-muted-foreground">{h.user_agent}</div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistoryTarget(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toggleTarget}
        onOpenChange={(v) => !v && setToggleTarget(null)}
        title={toggleTarget?.active ? `Deactivate ${toggleTarget?.name}?` : `Activate ${toggleTarget?.name}?`}
        description={toggleTarget?.active ? "They will immediately lose access to Hunter. History is preserved." : "They will regain access with their assigned role."}
        confirmText={toggleTarget?.active ? "Deactivate" : "Activate"}
        destructive={toggleTarget?.active}
        onConfirm={doToggleActive}
      />
    </div>
  );
}
